<template>
  <div class="full-width">
    <q-btn-dropdown
      color="primary-variant"
      size="sm"
      outline
      class="full-width ellipsis"
    >
      <template #label>
        <q-icon
          name="calendar_today"
          color="primary-variant"
          class="q-mr-sm"
        />
        <span class="block ellipsis">
          {{ getSelectedPeriodLabel(modelValue, hasDimensions, noDateDimensionLabel) }}
        </span>
      </template>
      <q-list>
        <div
          class="q-pa-xs text-center text-subtitle2 bg-primary-variant text-white"
        >
          {{ $t('insights.chartDateRangeSelector.listTitlePeriod') }}
        </div>
        <q-separator />
        <template v-if="hasDimensions">
          <q-select
            :model-value="periodDimension"
            :options="dimensions"
            :label="$t('insights.chartDateRangeSelector.dateRangeDimensionLabel')"
            outlined
            dense
            map-options
            emit-value
            @update:model-value="onChangePeriodDimension"
          />
          <q-separator />
        </template>
        <template
          v-if="showDateRangeOptions"
        >
          <template v-if="showInheritedPeriod">
            <q-item
              v-close-popup
              clickable
              :active="modelValue.preset === 'inherit'"
              active-class="bg-grey-2 text-bold"
              @click="onChangePeriod({preset:'inherit'})"
            >
              <q-item-section>
                <q-item-label>
                  {{ $t('insights.chartConfigurator.periodPresetInherit') }}
                </q-item-label>
              </q-item-section>
            </q-item>
            <q-separator />
          </template>
          <q-item
            v-for="period in periodPresets"
            :key="period.id"
            v-close-popup
            clickable
            :active="period.id === modelValue.preset"
            active-class="bg-grey-2 text-bold"
            @click="onChangePeriod(period)"
          >
            <q-item-section>
              <q-item-label>
                {{ period.label }}
              </q-item-label>
            </q-item-section>
          </q-item>
          <q-item
            v-close-popup
            clickable
            :active="modelValue.preset === 'custom'"
            active-class="bg-grey-2 text-bold"
            @click="onShowCustomDateRangeSelector"
          >
            <q-item-section>
              <q-item-label>
                {{ $t('insights.chartConfigurator.periodPresetCustom') }}
              </q-item-label>
            </q-item-section>
          </q-item>
        </template>
      </q-list>
    </q-btn-dropdown>
    <q-dialog
      v-model="showCustomDateRangeSelector"
      transition-show="scale"
      transition-hide="scale"
      :maximized="$q.platform.is.mobile"
    >
      <q-date
        v-model="customDateRange"
        mask="YYYY-MM-DD"
        :navigation-max-year-month="maxYearMonth"
        range
      >
        <div class="row items-center justify-end">
          <q-btn
            v-close-popup
            :label="$t('common.buttonCancel')"
            color="secondary"
            flat
          />
          <q-btn
            v-close-popup
            :label="$t('common.buttonApply')"
            color="primary"
            flat
            :disable="disableApply"
            @click="onSetCustomDateRange"
          />
        </div>
      </q-date>
    </q-dialog>
  </div>
</template>
<script>
import {
  chain, get, isEmpty, isObject,
} from 'lodash';
import { useTime } from 'src/composables/time';
import Insights from 'src/mixins/Insights';

export default {
  name: 'ChartDateRangeSelector',
  mixins: [Insights],

  props: {
    modelValue: {
      type: Object,
      required: true,
    },
    showInheritedPeriod: {
      type: Boolean,
      default: false,
    },
    dimensions: {
      type: Array,
      required: false,
      default: () => [],
    },
  },

  emits: ['update:modelValue'],

  setup() {
    const { getLocaleDateString, formatDate, extractDate, endOfDate } = useTime();
    return { getLocaleDateString, formatDate, extractDate, endOfDate };
  },

  data() {
    return {
      currentCustomDateRange: { from: null, to: null },
      showCustomDateRangeSelector: false,
    };
  },

  computed: {
    disableApply() {
      return (!this.currentCustomDateRange.from || !this.currentCustomDateRange.to);
    },

    hasDimensions() {
      return !isEmpty(this.dimensions);
    }, // hasDimensions()

    showDateRangeOptions() {
      return (!this.hasDimensions) || (this.periodDimension !== null);
    }, // showDateRangeOptions()

    noDateDimensionLabel() {
      return chain(this.dimensions).find({ value: null }).get('label').value();
    }, // noDateDimensionLabel()

    maxYearMonth() {
      return this.formatDate(Date.now(), 'yyyy/MM');
    }, // maxYearMonth()

    periodDimension() {
      return get(this.modelValue, 'periodDimension', null);
    }, // periodDimension()

    customDateRange: {
      get() {
        const from = this.currentCustomDateRange.from
          ? this.formatDate(this.currentCustomDateRange.from, 'yyyy-MM-dd')
          : '';
        const to = this.currentCustomDateRange.to
          ? this.formatDate(this.currentCustomDateRange.to, 'yyyy-MM-dd')
          : '';

        if ((from) && (from === to)) {
          return from;
        }

        return { from, to };
      },
      set(newValue) {
        if (!newValue) {
          this.currentCustomDateRange = { from: null, to: null };
          return;
        }

        if (!isObject(newValue)) {
          const fromDate = this.extractDate(newValue, 'yyyy-MM-dd');
          this.currentCustomDateRange.from = fromDate;
          this.currentCustomDateRange.to = this.endOfDate(fromDate, 'day');
        } else {
          this.currentCustomDateRange.from = this.extractDate(newValue.from, 'yyyy-MM-dd');
          this.currentCustomDateRange.to = this.endOfDate(this.extractDate(newValue.to, 'yyyy-MM-dd'), 'day');
        }
      },
    },
  },

  watch: {
    modelValue: {
      immediate: true,
      deep: true,
      handler(newValue) {
        this.currentCustomDateRange.from = get(newValue, 'fromTimestamp', null);
        this.currentCustomDateRange.to = get(newValue, 'toTimestamp', null);
      },
    },
  },

  methods: {
    onChangePeriod(periodObj) {
      if (isEmpty(periodObj)) {
        return;
      }
      this.$emit('update:modelValue', {
        ...this.modelValue,
        preset: periodObj.id,
        ...periodObj,
      });
    }, // onChangePeriod()

    onChangePeriodDimension(periodDimension) {
      this.$emit('update:modelValue', {
        ...this.modelValue,
        periodDimension,
      });
    }, // onChangePeriodDimension()

    onShowCustomDateRangeSelector() {
      this.showCustomDateRangeSelector = true;
    }, // onShowCustomDateRangeSelector()

    onSetCustomDateRange() {
      const { from, to } = this.currentCustomDateRange;
      if (from && to) {
        const fromLabel = this.getLocaleDateString(from);
        const toLabel = this.getLocaleDateString(to);
        const label = (fromLabel === toLabel) ? fromLabel : `${fromLabel} - ${toLabel}`;

        this.$emit('update:modelValue', {
          ...this.modelValue,
          label,
          id: 'custom',
          preset: 'custom',
          fromTimestamp: from,
          toTimestamp: to,
        });
      }
    }, // onSetCustomDateRange()

  },
};
</script>
